import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alerte, Bouton, ChampDate } from '../ui';
import { messageDErreur, useSession } from '../auth/session';
import { useTraduction } from '../i18n/langue';
import { maintenant } from '../horloge';
import { cleDuJour, dateComplete } from './dates';
import type { ParentDuDossier } from './types';

interface Reponse {
  at: string;
  parentSlot: number | null;
  source: 'schedule' | 'exception' | null;
}

/**
 * « Qui a l'enfant ? » — la question la plus posée du produit.
 *
 * Elle répond pour aujourd'hui par défaut, et pour n'importe quelle date à la demande.
 * La réponse est factuelle et ne commente jamais la situation.
 *
 * **La réponse est en tête, la commande en dessous.** Le volet s'ouvrait sur « À quelle
 * date ? » et ses trois listes déroulantes ; il fallait passer soixante pixels de
 * formulaire pour lire le seul fait qu'on venait chercher, écrit à la taille du corps de
 * texte. Or la question posée neuf fois sur dix est « et aujourd'hui ? », et elle a déjà
 * sa réponse à l'ouverture. L'écran répond donc d'abord, et propose ensuite de regarder
 * un autre jour.
 *
 * **Le sélecteur se replie.** Il ne se déplie qu'à la demande — ou de lui-même dès que le
 * jour affiché n'est pas aujourd'hui, sans quoi on ne verrait plus par quoi la date a
 * changé. « Revenir à aujourd'hui » est alors offert : sans lui, retrouver le jour courant
 * demanderait de régler trois listes pour revenir d'où l'on vient.
 *
 * **Le fait avant la date.** « Le lundi 31 août 2026, l'enfant est chez vous » faisait lire
 * six mots de calendrier avant l'information. La date passe en dessous, en second plan :
 * elle situe la réponse, et dit « Aujourd'hui » quand c'est le cas — ce qui compte
 * précisément parce que le jour affiché peut désormais ne pas être celui-ci.
 */
export function QuiALEnfant({
  caseFileId,
  parents,
  aujourdhui,
}: {
  caseFileId: string;
  parents: ParentDuDossier[];
  aujourdhui?: Date;
}) {
  const { client } = useSession();
  const { locale, messages } = useTraduction();
  const m = messages.dossier.ecran;
  const base = aujourdhui ?? maintenant();
  const cleAujourdhui = cleDuJour(base);
  const [jour, setJour] = useState(cleAujourdhui);
  const [deplie, setDeplie] = useState(false);

  // Midi local : la question porte sur une journée, pas sur un instant précis.
  const instant = new Date(`${jour}T12:00:00`);
  const cestAujourdhui = jour === cleAujourdhui;

  const reponse = useQuery({
    queryKey: ['qui', caseFileId, jour],
    queryFn: () =>
      client.request<Reponse>(`/case-files/${caseFileId}/custody/who`, {
        query: { at: instant.toISOString() },
      }),
    enabled: !Number.isNaN(instant.getTime()),
  });

  const parent = parents.find((p) => p.slot === reponse.data?.parentSlot);
  const sansSchema = reponse.data?.parentSlot === null || !parent;
  const date = dateComplete(instant, locale);

  return (
    <div className="qui-a-lenfant">
      {reponse.isError ? (
        <Alerte titre={m.quiErreur}>{messageDErreur(reponse.error)}</Alerte>
      ) : null}

      {/* Une seule région annoncée, qui porte le fait et la date : changer de jour doit
          s'entendre comme une phrase, pas comme deux annonces séparées dont la seconde
          arriverait sans son sujet. */}
      <div aria-live="polite">
        <p className={sansSchema ? 'qui-reponse absente' : 'qui-reponse'}>
          {reponse.isPending
            ? m.quiRecherche
            : sansSchema
              ? m.quiAucunSchema
              : parent.isViewer
                ? m.quiReponseVous
                : m.quiReponseAutre(parent.displayName)}
        </p>
        <p className="qui-date discret">
          {cestAujourdhui ? m.quiDateAujourdhui(date) : date}
        </p>
        {/* La dérogation tient sa propre ligne. Glissée en incise dans la phrase, elle
            allongeait la réponse d'une subordonnée pour une précision qu'on ne cherche
            qu'après avoir lu chez qui l'enfant se trouve. */}
        {!reponse.isPending && !sansSchema && reponse.data?.source === 'exception' ? (
          <p className="qui-derogation discret">{m.quiDerogation}</p>
        ) : null}
      </div>

      {deplie || !cestAujourdhui ? (
        <div className="qui-autre-jour">
          {/* On interroge le calendrier autour d'aujourd'hui, pas sur vingt-cinq ans. */}
          <ChampDate
            label={m.quiQuelleDate}
            value={jour}
            onChange={setJour}
            fenetre={{ avant: 1, apres: 1 }}
            traduction={messages.dates}
          />
          {!cestAujourdhui ? (
            <Bouton
              variante="lien"
              onClick={() => {
                setJour(cleAujourdhui);
                setDeplie(false);
              }}
            >
              {m.quiRevenirAujourdhui}
            </Bouton>
          ) : null}
        </div>
      ) : (
        <Bouton variante="secondaire" onClick={() => setDeplie(true)}>
          {m.quiAutreJour}
        </Bouton>
      )}
    </div>
  );
}
